import type { CSSProperties, JSX } from 'react';
import { useT } from '../i18n/index.js';
import './Wordmark.css';

/**
 * The lockup. Uppercase here is deliberate — the wordmark and game titles are
 * the only exceptions to sentence case (design spec §6).
 */

/** Fixed by compliance: 18+ badge and the no-cash-value claim travel together. */
export const STRAPLINE = '18+ · SIMULATED CHIPS · WALANG CASH VALUE';

export type WordmarkSize = 'sm' | 'md' | 'lg' | 'display';

export interface WordmarkProps {
  /** A step, or a cap height in px when a screen needs to fit an exact rail. */
  size?: WordmarkSize | number;
  /** Two lines is the splash lockup; one line is the chrome lockup. */
  lines?: 1 | 2;
  strapline?: boolean;
  className?: string;
}

export function Wordmark({
  size = 'md',
  lines = 1,
  strapline = false,
  className,
}: WordmarkProps): JSX.Element {
  const classes = ['wordmark-lockup'];
  if (className !== undefined) classes.push(className);

  // Every dimension of the treatment — stroke, offsets — scales off this one
  // value, so a px size stays a faithful letras lockup rather than a squashed one.
  const mark = typeof size === 'number' ? 'wordmark' : `wordmark wordmark--${size}`;
  const style =
    typeof size === 'number' ? ({ '--wordmark-size': `${size}px` } as CSSProperties) : undefined;

  return (
    <div className={classes.join(' ')}>
      <span className={mark} style={style} role="img" aria-label="Tira Pa More">
        {lines === 2 ? (
          <>
            <span className="wordmark__line">Tira</span>
            <span className="wordmark__line">Pa More</span>
          </>
        ) : (
          'Tira Pa More'
        )}
      </span>
      {strapline ? <Strapline /> : null}
    </div>
  );
}

export function Strapline({ className }: { className?: string }): JSX.Element {
  const t = useT();
  return (
    <p className={className === undefined ? 'wordmark-strapline' : `wordmark-strapline ${className}`}>
      {t('ui.strapline')}
    </p>
  );
}
